import { shouldDisplayParam } from 'utils/params';

// Category values under turbineParams[id] should always be arrays of param
// definitions, but the two producers of this state (legacy per-type xlsx
// groupBy vs. schema-driven AllRegisters) don't always agree - guard against
// either shape being missing or an unexpected non-array value so a spread
// here never throws "is not iterable".
const asArray = (value) => (Array.isArray(value) ? value : []);

const MONITORING_CATEGORIES = [
  'grid', 'revolution', 'revolutions', 'temperature', 'vibration', 'wind', 'condition', 'meter',
];

// Schema-driven turbines (type.schemaId set - see actions/details.js) never
// populate the legacy per-category keys below (their LOAD_WIND_TURBINE_PARAMS
// response is { State, Failure, AllRegisters } instead, see
// Orbital-server's TurbineSchemaRegister) - both LiveDataGraph.js (via
// getAllMonitoringParameters) and LiveControlsMenu.js (via
// getNormalizedTurbineParams, reading turbineParams.grid/.revolution/etc
// directly) expect those keys to exist either way, so this derives them once
// here from AllRegisters' own category/name/unit columns.
export const getNormalizedTurbineParams = (state, windTurbineId) => {
  const slice = state.turbineParams[windTurbineId];
  if (!slice) {
    return slice;
  }

  const hasLegacyCategories = MONITORING_CATEGORIES.some((key) => Array.isArray(slice[key]));
  if (hasLegacyCategories) {
    return slice;
  }

  // scale is required (2026-10-21): History's DataGraph and the live graph
  // divide the raw register values by it - without it, RAGO's history showed
  // wind/power 10x too high and production 1000x (scale 10 / 1000).
  const registers = asArray(slice.AllRegisters).map((r) => ({
    dbname: r.dbname,
    name: r.name || r.dbname,
    unit: r.unit || '',
    scale: r.scale || 1,
  }));
  const byCategory = {};
  MONITORING_CATEGORIES.forEach((key) => { byCategory[key] = []; });
  asArray(slice.AllRegisters).forEach((r, i) => {
    if (MONITORING_CATEGORIES.includes(r.category)) {
      byCategory[r.category].push(registers[i]);
    }
  });

  return { ...slice, ...byCategory };
};

export const getAllMonitoringParameters = (state, windTurbineId) => {
  const monitoringState = getNormalizedTurbineParams(state, windTurbineId);
  if (!monitoringState) {
    return [];
  }

  // LiveDataGraph.js unconditionally looks up dbname 'power' and 'wind' as
  // its two default plotted series - correctly tagged 'grid'/'wind' (see
  // Orbital-server's turbineSchemas/xlsx/ntk300.xlsx, matched against the
  // manufacturer's own Monitoring sheet), so both already come through the
  // category spread below - no special-casing needed.
  const monitoringParams = [
    ...asArray(monitoringState.grid),
    ...asArray(monitoringState.revolution),
    ...asArray(monitoringState.revolutions),
    ...asArray(monitoringState.temperature),
    ...asArray(monitoringState.vibration),
    ...asArray(monitoringState.wind),
    ...asArray(monitoringState.condition),
    ...asArray(monitoringState.meter),
  ];

  return monitoringParams.filter(shouldDisplayParam);
};

export const getTurbineParams = (state, windTurbineId) => {
  const turbineParamsState = state.turbineParams[windTurbineId];
  if (!turbineParamsState) {
    return [];
  }

  const windTurbine = state.windTurbines[windTurbineId];
  const valueFor = (dbname) => (windTurbine && windTurbine.params ? windTurbine.params[dbname] : 'N/A');

  if (turbineParamsState.Parameter) {
    return turbineParamsState.Parameter.map(p => ({
      dbname: p.dbname,
      number: p.number,
      name: p.text,
      default: p.default,
      value: valueFor(p.dbname),
      min: p.min,
      max: p.max,
      unit: p.unit,
      scale: p.scale
    }));
  }

  // Schema-driven turbines have no "Parameter" sheet equivalent - Holding
  // registers (settings, as opposed to read-only telemetry Input registers)
  // are the closest match, from the same AllRegisters bucket
  // getAllMonitoringParameters already reads. min/max/default_value are
  // optional on TurbineSchemaRegister (see database/init.sql) - a source
  // that never documented them (e.g. RAGO, reverse-engineered from a Python
  // script) still comes through undefined here, same as before this existed.
  // `number` has no schema-driven equivalent either, but is only ever used
  // as an opaque key for onEditParameter's turbineParams.find(...) lookup -
  // dbname works identically there, just a string instead of an index.
  return asArray(turbineParamsState.AllRegisters)
    .filter(r => r.registerType === 'holding')
    .map(r => ({
      dbname: r.dbname,
      number: r.dbname,
      name: r.name || r.dbname,
      default: r.default_value === null ? undefined : r.default_value,
      value: valueFor(r.dbname),
      min: r.min === null ? undefined : r.min,
      max: r.max === null ? undefined : r.max,
      address: r.address,
      unit: r.unit || '',
      scale: r.scale || 1,
    }));
};
